// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { useFloatingTree } from "@floating-ui/react";
import {
    isModifiedClick,
    isSamePagePath,
    replaceLocationHash,
    scrollPageToTop,
    scrollToCategoryAnchorWhenReady,
} from "@stoneage/XDL/Utilities";
import { MouseEvent, useCallback } from "react";

export interface MenuLinkClickOptions {
    href?: string;
    /** Category anchor to scroll to instead of the top of the page. */
    anchorId?: string;
    openInNewWindow?: boolean;
}

/**
 * XNG-Changes: shared click behaviour for every navigation link inside the menus.
 *
 * Two problems it solves:
 * 1. The dropdown only auto-closes when `location.pathname` changes, and mega menu
 *    links sit outside <MenuItem> so they never emit the floating-ui tree click
 *    either — a link pointing at the current page left the panel open.
 * 2. Navigating to the page you are already on is a no-op for the router, so a
 *    link to the current category did nothing when the user had scrolled down.
 *
 * Returns true when the click was handled here (i.e. navigation was prevented).
 */
export const useMenuLinkClick = () => {
    const tree = useFloatingTree();

    return useCallback(
        (event: MouseEvent<HTMLElement>, { href, anchorId, openInNewWindow }: MenuLinkClickOptions = {}) => {
            // Always close the menu — a same-page link never triggers the pathname watcher.
            tree?.events.emit("click");

            if (!href || openInNewWindow || isModifiedClick(event) || !isSamePagePath(href)) {
                return false;
            }

            event.preventDefault();

            if (anchorId) {
                replaceLocationHash(anchorId);
                scrollToCategoryAnchorWhenReady(anchorId);
                return true;
            }

            // Same page, no anchor: drop any leftover category hash and go back up.
            replaceLocationHash();
            scrollPageToTop();
            return true;
        },
        [tree],
    );
};

export default useMenuLinkClick;
